import { useMedia } from '@ssv-website/hooks';
import { DialogPlugin } from 'tdesign-react';
import './GuestCardGrid.css';
import { CloseIcon } from 'tdesign-icons-react';
import { GuestItem, MeetingStatus } from '../type';
import { getCMSIntlVal } from '../utils/common';
import { useLocale } from 'use-intl';
import { useMeetingStatus } from '../context/MeetingConfigContext';
import Image from '../../../image';

const GuestCardItem = ({ item }: { item: GuestItem }) => {
  const isMobile = useMedia('mobile');
  const locale = useLocale();
  const { avatar } = item;
  const title: string = getCMSIntlVal(item, 'title', locale);
  const intro = getCMSIntlVal(item, 'intro', locale);
  const name = getCMSIntlVal(item, 'name', locale);
  const isEN = locale === 'en';
  const titles = title.split(';') || [];
  const status = useMeetingStatus();

  const showIntroduction = [
    MeetingStatus.NotStarted,
    MeetingStatus.InProgress,
  ].includes(status);

  const handleClick = () => {
    if (isMobile && showIntroduction) {
      const dialogPluginInstance = DialogPlugin.alert({
        closeOnOverlayClick: true,
        closeBtn: false,
        placement: 'center',
        body: (
          <div
            className="relative p-8 pr-2 h-[400px] flex flex-col"
            style={{
              background:
                'url(https://cdn-digital.ssv.qq.com/upload//s2025/guest-hover-bg1.png) no-repeat left top / 100% auto',
            }}
          >
            <div
              className="absolute right-4 top-4 flex items-center justify-center text-[#121C59] cursor-pointer"
              onClick={() => {
                dialogPluginInstance.hide();
              }}
            >
              <CloseIcon size={20}></CloseIcon>
            </div>
            <h5 className="text-[20px] leading-[1.5] text-[#121C59] pb-2 flex-shrink-0">
              {name}
            </h5>
            <div className="flex-1 min-h-0 overflow-y-scroll scrollbarStyle1 scroll-smooth pr-6">
              {isEN && (
                <div className="text-[#121C59]/80">
                  {titles.map((item, index) => (
                    <p
                      key={index}
                      className="text-sm font-300 break-words hyphens-auto"
                    >
                      {item}
                    </p>
                  ))}
                  <div className="h-px bg-[#121C59] w-full my-2 opacity-20"></div>
                </div>
              )}
              <p className="text-sm text-[#121C59]/80 font-300">{intro}</p>
            </div>
          </div>
        ),
        width: '80%',
        dialogClassName: 'custom-dialog',
        style: {
          padding: '0',
          width: '80%',
          background: '#1C3FF0',
          border: 'none',
        },
        showOverlay: true,
        preventScrollThrough: true,
        header: false,
        footer: false,
        onClose: () => {
          dialogPluginInstance.hide();
        },
      });
    }
  };
  return (
    <div
      className="relative w-[265px] h-[384px] group cursor-pointer overflow-hidden mobile:w-[181px] mobile:h-[260px]"
      style={{
        background:
          'linear-gradient(144deg, #9B96FD 1.68%, #6492FF 33.73%, #82D6FD 57.86%, #C8EEFF 73.93%)',
      }}
      onClick={handleClick}
    >
      {/* 人物头像 */}
      <Image
        src={avatar}
        alt={name}
        loading="lazy"
        className="!w-[265px] !h-[265px] mobile:!w-[181px] mobile:!h-[181px]"
        width={265}
        height={265}
        style={{
          background: 'transparent',
        }}
      ></Image>
      {/* 人物信息 */}
      <div
        className="absolute z-[1] bottom-0 left-0 w-full h-[124px] p-[16px] flex flex-col mobile:p-[12px] mobile:h-[95px]"
        style={{
          background:
            'url(https://cdn-digital.ssv.qq.com/upload//s2025/guest-hover-bg1.png) no-repeat left top / 100% auto',
        }}
      >
        <div className="text-[20px] leading-[1.5] text-[#121C59] mb-[8px] mobile:text-[12px] break-words hyphens-auto">
          {name}
        </div>
        <div className="text-[12px] text-[#121C59]/80 font-300 mobile:text-[10px] mobile:text-[#121C59]/60 line-clamp-3">
          {titles.map((item, index) => (
            <p
              key={index}
              className="leading-[1.5] font-300 break-words hyphens-auto"
            >
              {item}
            </p>
          ))}
        </div>
      </div>
      {/* 人物简介 */}
      {showIntroduction && (
        <div
          className="absolute z-[2] bottom-0 left-0 w-full h-full flex flex-col px-6 pl-4 pr-1 pt-8 translate-y-[200px] invisible group-hover:visible transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-y-0 duration-100 ease-in mobile:hidden"
          style={{
            background:
              'url(https://cdn-digital.ssv.qq.com/upload//s2025/guest-hover-bg1.png) no-repeat left top / 100% 100%',
          }}
        >
          <div className="text-[20px] leading-[1.5] text-[#121C59] mb-2 flex-shrink-0 break-words hyphens-auto">
            {name}
          </div>
          <div className="flex-1 min-h-0 scrollbarStyle1 overflow-y-auto pr-3 pb-6">
            {isEN && (
              <div className="text-[#121C59]/80">
                {titles.map((item, index) => (
                  <p
                    key={index}
                    className="text-[12px] leading-[1.5] font-300 break-words hyphens-auto"
                  >
                    {item}
                  </p>
                ))}
                <div className="h-px bg-[#121C59] w-full my-2 opacity-20"></div>
              </div>
            )}

            <div className="text-[12px] leading-[1.5] text-[#121C59]/80 font-300 break-words hyphens-auto">
              {intro}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function GuestCardGrid({ list }: { list: GuestItem[] }) {
  return (
    <div className="w-fit grid grid-cols-5 gap-x-[12px] gap-y-[56px] pt-[56px] mobile:gap-1 mobile:pt-6 mobile:pr-5 mobile:grid-cols-[repeat(5,minmax(min-content,max-content))]">
      {list.map((item) => (
        <GuestCardItem key={item.id} item={item} />
      ))}
    </div>
  );
}
